import React, { useMemo, useState } from 'react';
import { CHAPTERS, SHOT_LIST } from './manualContent';
import ManualFigure, { shotCount } from './ManualFigure';
import './ManualView.css';

// The user manual, rendered from manualContent.js. Chapter list on the left,
// one chapter at a time on the right, plus a search that jumps to whatever
// mentions the word. "Print this manual" renders every chapter at once so the
// whole thing comes out as one document.

function Section({ section }) {
  switch (section.type) {
    case 'prose':
      return <p className="mn-p">{section.text}</p>;

    case 'steps':
      return (
        <div className="mn-steps">
          {section.title && <h4 className="mn-steps-title">{section.title}</h4>}
          <ol>{section.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
        </div>
      );

    case 'figure':
      return <ManualFigure id={section.id} caption={section.caption} />;

    case 'note':
      return (
        <div className={`mn-note mn-note--${section.tone || 'tip'}`}>
          <span className="mn-note-tag">{section.tone === 'warn' ? 'Important' : 'Tip'}</span>
          <span>{section.text}</span>
        </div>
      );

    case 'table':
      return (
        <div className="mn-table-wrap">
          <table className="mn-table">
            <thead><tr>{section.head.map(h => <th key={h}>{h}</th>)}</tr></thead>
            <tbody>
              {section.rows.map((r, i) => (
                <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    default:
      return null;
  }
}

function Chapter({ chapter, index }) {
  return (
    <article className="mn-chapter" id={`mn-${chapter.id}`}>
      <header className="mn-chapter-head">
        <span className="mn-chapter-num">{String(index + 1).padStart(2, '0')}</span>
        <div>
          <h2 className="mn-chapter-title">{chapter.title}</h2>
          {chapter.blurb && <p className="mn-chapter-blurb">{chapter.blurb}</p>}
        </div>
      </header>
      {chapter.sections.map((s, i) => <Section key={i} section={s} />)}
    </article>
  );
}

// Flattened text per chapter, so search can match anything in it.
function chapterText(ch) {
  const bits = [ch.title, ch.blurb];
  for (const s of ch.sections) {
    if (s.text) bits.push(s.text);
    if (s.title) bits.push(s.title);
    if (s.steps) bits.push(...s.steps);
    if (s.caption) bits.push(s.caption);
    if (s.head) bits.push(...s.head);
    if (s.rows) bits.push(...s.rows.flat());
  }
  return bits.join(' ').toLowerCase();
}

export default function ManualView() {
  const [activeId, setActiveId] = useState(CHAPTERS[0].id);
  const [query, setQuery] = useState('');
  const [printAll, setPrintAll] = useState(false);

  const index = useMemo(() => CHAPTERS.map(ch => ({ id: ch.id, text: chapterText(ch) })), []);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return new Set(index.filter(c => c.text.includes(q)).map(c => c.id));
  }, [query, index]);

  const shown = CHAPTERS.filter(ch => !matches || matches.has(ch.id));
  const active = CHAPTERS.find(ch => ch.id === activeId) || CHAPTERS[0];
  const activeIdx = CHAPTERS.indexOf(active);
  const missingShots = SHOT_LIST.length - shotCount();

  function go(id) {
    setActiveId(id);
    setPrintAll(false);
    document.querySelector('.mn-body')?.scrollTo({ top: 0 });
  }

  function printManual() {
    setPrintAll(true);
    // Let the full document paint before handing off to the print dialog.
    setTimeout(() => window.print(), 60);
  }

  return (
    <div className={`mn-wrap${printAll ? ' mn-wrap--printing' : ''}`}>
      <aside className="mn-nav">
        <input
          className="mn-search"
          type="search"
          placeholder="Search the manual…"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />

        {matches && shown.length === 0 && (
          <p className="mn-nav-empty">Nothing matches “{query}”.</p>
        )}

        <ol className="mn-nav-list">
          {shown.map(ch => (
            <li key={ch.id}>
              <button
                type="button"
                className={`mn-nav-item${ch.id === activeId && !printAll ? ' mn-nav-item--active' : ''}`}
                onClick={() => go(ch.id)}
              >
                <span className="mn-nav-num">{String(CHAPTERS.indexOf(ch) + 1).padStart(2, '0')}</span>
                {ch.title}
              </button>
            </li>
          ))}
        </ol>

        <button type="button" className="mn-print-btn" onClick={printManual}>
          Print the whole manual
        </button>

        {missingShots > 0 && (
          <p className="mn-nav-hint">
            {missingShots} of {SHOT_LIST.length} screenshots not added yet — diagrams are shown
            in their place. See <code>src/Assets/manual/README.md</code>.
          </p>
        )}
      </aside>

      <div className="mn-body">
        {printAll ? (
          <>
            <div className="mn-cover">
              <h1>REKON</h1>
              <p>User Manual</p>
            </div>
            {CHAPTERS.map((ch, i) => <Chapter key={ch.id} chapter={ch} index={i} />)}
          </>
        ) : (
          <>
            <Chapter chapter={active} index={activeIdx} />
            <nav className="mn-pager">
              {activeIdx > 0 && (
                <button type="button" className="mn-pager-btn" onClick={() => go(CHAPTERS[activeIdx - 1].id)}>
                  ← {CHAPTERS[activeIdx - 1].title}
                </button>
              )}
              {activeIdx < CHAPTERS.length - 1 && (
                <button type="button" className="mn-pager-btn mn-pager-btn--next" onClick={() => go(CHAPTERS[activeIdx + 1].id)}>
                  {CHAPTERS[activeIdx + 1].title} →
                </button>
              )}
            </nav>
          </>
        )}
      </div>
    </div>
  );
}
